<script type="text/jsx">
import _ from 'lodash';
import { isEmptyVal } from '@/utils';
import { BILLING_CYCLE } from '@/common/constants';
import { getPlanUnitWithCycle, planUnitMap } from '@/common/product-unit';
import SaveCount from '@/components/save-count';

export default {
  name: 'config-custom-group',
  components: { SaveCount },
  model: {
    prop: 'groupValue',
    event: 'syncGroupValue',
  },
  props: {
    groupCategoryKey: String,
    categoryList: {
      type: Array,
      required: true,
      default: () => [],
    },
    groupValue: String,
    showGroupAlert: Boolean,
    unit: {
      type: Number,
      default: planUnitMap.license,
      validator: function(v) {
        return _.chain(planUnitMap)
          .values()
          .includes(v)
          .value();
      },
    },
    trackingPlanName: {
      type: String,
      required: false,
      default: '',
    },
    selectGroupTitle: String,
    axSelectGroupTitle: String,
  },
  computed: {
    currentPlanList() {
      return _.map(this.categoryList, (p) => p.plan);
    },
    groupByPlanListByGroupCategoryKey() {
      return _.groupBy(this.currentPlanList, (p) => p?.[this.groupCategoryKey]);
    },
    firstDisabledCategoryAlertItem() {
      return _.find(this.categoryList, (p) => p.disabled && !isEmptyVal(p.alert));
    },
  },
  methods: {
    mx_renderGroupCol(item = {}) {
      let { plan = {}, disabled = false, tooltips = '', disabledInnerDesc = '', alert = '' } = item;

      const saveProps = plan.billing_cycle === BILLING_CYCLE.ANNUAL ? {
        disabled,
        monthlyPrice: _.get(this.groupByPlanListByGroupCategoryKey, [BILLING_CYCLE.MONTHLY, 0, 'amount']),
        annualPrice: plan.amount,
      } : {};

      const planIsSelected = plan?.cartInfo?.selected_number > 0;

      // support current plan have been selected, but the current have been disabled case
      if (planIsSelected) disabled = false;

      const cycleInnerVNode = disabledInnerDesc ?
        <p class="opc-custom-group__disabled--desc">{disabledInnerDesc}</p> :
        <p class="opc-custom-group__plan__price">
              <span class="opc-custom-group__plan__price--amount">
                {this.$money(plan.amount, { forceDiff: true })}
              </span>
          <span class="opc-custom-group__plan__price--unit">
            {this.$t(plan.billing_cycle ? getPlanUnitWithCycle(plan.billing_cycle, this.unit) : '')}<priceVATTip />
              </span>
        </p>;

      const cycleRadioVNode = <zm-radio
        data-testid="checkout-radio-35"
        disabled={disabled}
        label={plan[this.groupCategoryKey]}
      >
        <p class="opc-custom-group__plan__title">
          {this.$t(plan.billing_cycle ? `billing.billing_cycle.${plan.billing_cycle}` : '')}
          <save-count
            class="opc-custom-group__plan__save"
            {...{ props: saveProps }}
          />
        </p>
        {cycleInnerVNode}
      </zm-radio>;

      const shouldShowTips = (this.showCycleAlert && isEmptyVal(alert) && !isEmptyVal(tooltips))
        || (!isEmptyVal(tooltips) && !this.showCycleAlert);

      if (shouldShowTips) {
        return <zm-tooltip key={plan.plan_code}>
          <p slot="content" domPropsInnerHTML={tooltips}></p>
          {cycleRadioVNode}
        </zm-tooltip>;
      }

      return cycleRadioVNode;
    },
    mx_renderAlert(slot, title) {
      if (slot) return slot;
      if (title) return <zm-alert
        class="opc-common-info-alert"
        type="warning"
        closable={false}
        show-icon>
        <p domPropsInnerHTML={title} onClick={this.trackingAlert} />
      </zm-alert>;
    },
    mx_renderGroupDisabledAlert() {
      if (!this.showGroupAlert || !this.firstDisabledCategoryAlertItem) return null;
      return this.mx_renderAlert(this.$slots?.groupAlert, this.firstDisabledCategoryAlertItem?.alert);
    },
    switchGroupCategory(oldPlan, newPlan) {
      this.$emit('switchCategoryAction', oldPlan, newPlan);
    },
    changeGroupValue(val) {
      this.$emit('syncGroupValue', val);
    },
    trackingAlert(e) {
      const { tagName = '', className, text = '' } = e?.target || {};
      const { alert } = this.firstDisabledCategoryAlertItem || {};

      if (
        tagName?.toLowerCase() === 'a'
        && className === 'opc-go-back__billing'
        && this.showGroupAlert
        && alert
      ) {
        this.$tracking.buyInteraction(
          `banner - product level`,
          `${this.trackingPlanName ? `${this.trackingPlanName} - ` : ''}${alert} - ${text}`,
        );
      }
    },
  },
  render() {
    const classList = ['opc-card-radio', 'opc-custom-group__plan', `opc-custom-group__plan__${this.currentPlanList?.length || 2}`];

    return <div class="opc-custom-group">
      {this.selectGroupTitle ?
        <p id="opc-custom-group__title" class="opc-custom-group__title">{this.selectGroupTitle}</p> : null}
        <custom-radio-group
          enter-select
          value={this.groupValue}
          onInput={this.changeGroupValue}
          plans={this.currentPlanList}
          action={this.switchGroupCategory}
          aria-label={this.axSelectGroupTitle || this.selectGroupTitle}
          groupCategoryKey={this.groupCategoryKey}
          class={classList}>
          {_.map(this.categoryList, (p) => this.mx_renderGroupCol(p))}
        </custom-radio-group>
        {this.mx_renderGroupDisabledAlert()}
    </div>;
  },
  watch: {
    firstDisabledCategoryAlertItem: {
      immediate: true,
      handler(item) {
        if (this.showGroupAlert && item?.alert) {
          this.$tracking.buyInteraction(
            'banner - product level',
            `${this.trackingPlanName ? `${this.trackingPlanName} - ` : ''}${item.alert} - impression`,
          );
        }
      },
    },
  },
};
</script>

<style lang="scss">
.opc-custom-group {
  margin-top: 16px;

  &__title {
    display: flex;
    align-items: center;
    font-weight: 700;
    font-size: 18px;
    line-height: 150%;
    color: #00053D;
    height: 100%;
  }

  &__plan {
    margin-top: 8px;

    &__2.opc-card-radio {
      gap: 16px;
    }

    &__3.opc-card-radio {
      gap: 12px;
    }

    .zm-radio {
      flex: 1;
      min-width: 0;

      &__wrap {
        width: 100%;
      }

      &__label {
        width: 100%;

        &--inner {
          width: 100%;
          /* padding-right: 2px !important; */
        }

        &.is-disabled {
          .opc-custom-group__plan__price--amount,
          .opc-custom-group__plan__price--unit {
            color: #9191A4;
          }
        }
      }
    }

    &__title {
      font-weight: 700;
      font-size: 16px;
      text-transform: capitalize;
      display: flex;
      align-items: center;
      flex-wrap: wrap; // for i18n
    }

    &__price {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;

      &--amount, &--unit {
        color: #00053D;
        line-height: 150%;
      }

      &--amount {
        font-weight: 700;
        font-size: 22px;
        word-break: break-all;
      }

      &--unit {
        font-weight: 500;
        font-size: 12px;
        color: #515079;
        word-break: break-all;
      }
    }
  }

  &__disabled--desc {
    font-family: Almaden Sans;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    letter-spacing: 0em;
    text-align: left;
    color: #515079;
    margin-top: 8px;
  }

  &__plan__save {
    margin-left: 4px;
  }
}
</style>
